/**
 * CSS animation duration constants for content slides.
 *
 * Belongs here: timing tokens + optional per-transition overrides (CSS vars + JS resolve).
 * Does not belong here: slide direction state or which screen uses which duration.
 */
import type { CSSProperties } from 'react'

let cachedSlideDurationMs: number | null = null
let cachedSlideOutDurationMs: number | null = null

function getCssDurationVar(name: string, fallbackMs: number): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') return fallbackMs
  const root = document.documentElement
  const raw = getComputedStyle(root).getPropertyValue(name).trim()
  if (!raw) return fallbackMs
  if (raw.endsWith('ms')) {
    const parsed = Number.parseFloat(raw.slice(0, -2))
    return Number.isFinite(parsed) ? parsed : fallbackMs
  }
  if (raw.endsWith('s')) {
    const parsed = Number.parseFloat(raw.slice(0, -1))
    return Number.isFinite(parsed) ? parsed * 1000 : fallbackMs
  }
  const parsed = Number.parseFloat(raw)
  return Number.isFinite(parsed) ? parsed : fallbackMs
}

export function getContentSlideDurationMs(): number {
  if (cachedSlideDurationMs != null) return cachedSlideDurationMs
  cachedSlideDurationMs = getCssDurationVar('--content-transition-duration', 480)
  return cachedSlideDurationMs
}

export function getContentSlideOutDurationMs(): number {
  if (cachedSlideOutDurationMs != null) return cachedSlideOutDurationMs
  cachedSlideOutDurationMs = getCssDurationVar('--content-transition-out-duration', 440)
  return cachedSlideOutDurationMs
}

/** Optional per-instance overrides — keep CSS animation and JS timeouts in sync. */
export type ContentSlideDurations = {
  inMs?: number
  outMs?: number
}

/**
 * Local CSS-var overrides for slide in/out. Apply on the animated subtree root.
 * Pair with `resolveContentSlideOutDurationMs` / `resolveContentSlideInDurationMs` for timeouts.
 */
export function contentSlideDurationStyle(durations?: ContentSlideDurations): CSSProperties {
  if (durations == null) return {}
  return {
    ...(durations.inMs != null
      ? ({ '--content-transition-duration': `${durations.inMs}ms` } as CSSProperties)
      : null),
    ...(durations.outMs != null
      ? ({ '--content-transition-out-duration': `${durations.outMs}ms` } as CSSProperties)
      : null)
  }
}

/** JS timeout length for slide-out — override or root CSS default. */
export function resolveContentSlideOutDurationMs(outMsOverride?: number): number {
  return outMsOverride ?? getContentSlideOutDurationMs()
}

/** JS timeout length for slide-in — override or root CSS default. */
export function resolveContentSlideInDurationMs(inMsOverride?: number): number {
  return inMsOverride ?? getContentSlideDurationMs()
}

/** Delay before confirm actions so slide-in can finish. */
export const CONFIRM_ACTION_DELAY_MS = 250
